import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Privacy Policy - SELF Labs</title>
        <meta name="description" content="SELF Phone Privacy Policy - Effective Date: 4/1/25" />
      </Helmet>

      {/* Background Effects */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.1)_0%,transparent_100%)]" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.03)_0%,transparent_1px)] bg-[length:24px_24px]" />

      <div className="relative">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
              📜 SELF Phone Privacy Policy
            </h1>
            <p className="text-indigo-400 mb-8">Effective Date: 4/1/25</p>

            <div className="space-y-12">
              {/* Introduction */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">1. Introduction</h2>
                <div className="prose prose-invert max-w-none">
                  <p>This Privacy Policy ("Policy") describes how SELF Labs Inc. ("SELF Labs," "we," "us," or "our") collects, uses, protects, and shares your information when you use the SELF Phone, the Role Model AI App Store, the Harmony Signal Engine, and participate in the Signal Economy.</p>
                  <p>By using the SELF Phone and associated services, you agree to the terms outlined in this Policy.</p>
                </div>
              </section>

              {/* What Data We Collect */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">2. What Data We Collect</h2>
                <div className="prose prose-invert max-w-none">
                  <p>We may collect and process the following categories of information:</p>
                  
                  <h3 className="text-xl font-semibold mb-3 text-indigo-300">Device Information:</h3>
                  <ul>
                    <li>Hardware identifiers (device ID, model)</li>
                    <li>Operating system version (SELF OS)</li>
                    <li>Connection status (mesh participation, satellite signal)</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3 text-indigo-300">Account Information:</h3>
                  <ul>
                    <li>Wallet address (for ESSENCE rewards)</li>
                    <li>Optional email address (if user provides during onboarding)</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3 text-indigo-300">Activity Information:</h3>
                  <ul>
                    <li>Participation in Harmony Mesh tasks (relay counts, validation actions)</li>
                    <li>Role Model AI App Store downloads and installations (non-personalized)</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3 text-indigo-300">Signal Metadata (Optional):</h3>
                  <ul>
                    <li>Encrypted execution metadata for agent task triggers (non-human readable)</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3 text-indigo-300">Financial Information:</h3>
                  <ul>
                    <li>ESSENCE earnings (on-chain public but linked privately to user-controlled wallets)</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3 text-indigo-300">Developer Submissions (if applicable):</h3>
                  <ul>
                    <li>Submitted app metadata, ownership information</li>
                  </ul>
                </div>
              </section>

              {/* How We Use Your Information */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">3. How We Use Your Information</h2>
                <div className="prose prose-invert max-w-none">
                  <p>We use your information to:</p>
                  <ul>
                    <li>Operate the Harmony Signal Engine and manage Mesh participation</li>
                    <li>Distribute ESSENCE rewards based on validated activity</li>
                    <li>Manage the Role Model AI App Store ecosystem</li>
                    <li>Enable Aura Shield™ encryption and optional data monetization controls</li>
                    <li>Improve system functionality, security, and user experience</li>
                    <li>Communicate updates, upgrades, or changes to the SELF Phone system</li>
                  </ul>
                </div>
              </section>

              {/* Data Sovereignty and Control */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">4. Data Sovereignty and Control</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Labs operates under a Joint Data Controller model:</p>
                  <p>You retain primary ownership and control over your personal data.</p>
                  <p>SELF Labs acts as a Joint Controller solely to:</p>
                  <ul>
                    <li>Facilitate encrypted storage</li>
                    <li>Enable optional data monetization</li>
                    <li>Execute and validate Harmony Signal tasks</li>
                  </ul>
                  <p>You control whether your data is:</p>
                  <ul>
                    <li>Locked (private and not shared)</li>
                    <li>Monetized (opt-in)</li>
                    <li>Deleted (full deletion available upon request or device reset)</li>
                  </ul>
                  <p>Aura Shield™ allows real-time management of your privacy settings through the SELF Phone interface.</p>
                </div>
              </section>

              {/* Data Monetization */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">5. Data Monetization (Optional)</h2>
                <div className="prose prose-invert max-w-none">
                  <p>If you choose to monetize certain data categories (e.g., agent signal logs, anonymized app usage metadata):</p>
                  <ul>
                    <li>Your data will only be listed with your consent</li>
                    <li>Data transactions occur through encrypted, privacy-preserving layers</li>
                    <li>You receive 98% of monetization proceeds</li>
                    <li>1% of proceeds go toward National Debt Reduction efforts</li>
                    <li>1% supports the SELF Labs DAO ecosystem</li>
                  </ul>
                  <p>You can opt out of monetization at any time through your Aura Shield™ dashboard.</p>
                </div>
              </section>

              {/* Data Sharing */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">6. Data Sharing</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Labs does not sell your personal data to third parties.</p>
                  <p>We may share limited encrypted data (non-human readable) only in the following cases:</p>
                  <ul>
                    <li>To validate agent task completions (within the decentralized Mesh network)</li>
                    <li>To process ESSENCE reward distributions</li>
                    <li>To comply with legal obligations or regulatory requirements</li>
                  </ul>
                  <p>All third-party processors (e.g., blockchain payment processors) must meet strict encryption and data protection standards.</p>
                </div>
              </section>

              {/* Security Measures */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">7. Security Measures</h2>
                <div className="prose prose-invert max-w-none">
                  <p>We protect your information using:</p>
                  <ul>
                    <li>Zero-Knowledge Encryption (PureCipher OmniSeal™)</li>
                    <li>Immutable Storage (Rubidex™ Data Fabric)</li>
                    <li>Encrypted transmission across Harmony Mesh</li>
                    <li>Local hardware security on the SELF Phone</li>
                    <li>Decentralized backup protocols</li>
                  </ul>
                  <p>Despite these efforts, no method of digital transmission or storage is 100% secure.</p>
                  <p>You use the SELF Phone and associated services at your own risk.</p>
                </div>
              </section>

              {/* Your Rights */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">8. Your Rights</h2>
                <div className="prose prose-invert max-w-none">
                  <p>Depending on your location, you have rights under GDPR, CCPA, and other regulations, including:</p>
                  <ul>
                    <li>Right to access your data</li>
                    <li>Right to correct inaccurate data</li>
                    <li>Right to delete your data</li>
                    <li>Right to opt-out of data monetization</li>
                    <li>Right to portability (download your own data)</li>
                  </ul>
                  <p>Requests to exercise your rights may be submitted to:</p>
                  <p>📩 [Insert Privacy Contact Email]</p>
                </div>
              </section>

              {/* Children's Privacy */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">9. Children's Privacy</h2>
                <div className="prose prose-invert max-w-none">
                  <p>The SELF Phone and its associated services are not intended for users under 18.</p>
                  <p>We do not knowingly collect information from minors.</p>
                  <p>If we discover that data has been collected from a minor without verified parental consent, it will be deleted.</p>
                </div>
              </section>

              {/* Changes to Privacy Policy */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">10. Changes to This Privacy Policy</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Labs may update this Policy from time to time.</p>
                  <p>We will notify you of any material changes through the SELF Phone interface or by email.</p>
                  <p>Continued use of the SELF Phone after updates constitutes acceptance of the new Policy.</p>
                </div>
              </section>

              {/* Contact Information */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">11. Contact Information</h2>
                <div className="prose prose-invert max-w-none">
                  <p>For questions, data rights requests, or privacy concerns, contact:</p>
                  <p>SELF Labs Inc.</p>
                  <p>🏢 Address: 1209 N Orange St, Wilmington, Delaware</p>
                  <p>📩 Email: data@rolemodel.ai</p>
                </div>
              </section>
            </div>
          </motion.div>

          {/* Footer */}
          <div className="mt-20 pt-12 border-t border-indigo-500/20">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">SELF Labs Inc.</h3>
                  <p className="text-gray-400">Powered by Role Model AI + Aura Protocol</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">Legal</h3>
                  <div className="space-y-2">
                    <Link to="/terms" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Terms & Conditions
                    </Link>
                    <Link to="/privacy" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Privacy Policy
                    </Link>
                    <Link to="/harmony-litepaper" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Harmony Litepaper
                    </Link>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">Contact</h3>
                  <div className="space-y-2">
                    <a href="mailto:data@rolemodel.ai" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      data@rolemodel.ai
                    </a>
                    <a href="https://x.com/selflabsinc" target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      @selflabsinc
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-12 pt-8 border-t border-indigo-500/20 text-center text-sm text-gray-400">
                <p>&copy; {new Date().getFullYear()} SELF Labs Inc. All rights reserved.</p>
                <p className="mt-2">Protected by PureCipher's OMNISEAL™ encryption suite</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage; 